import { FileText, PenLine, BarChart3, type LucideIcon } from "lucide-react";

export type ModuleStatus = "live";

export type Module = {
  slug: string;
  name: string;
  short: string; // tile / nav label
  pillar: string;
  icon: LucideIcon;
  /** one-line value prop used on tiles + nav */
  tagline: string;
  /** longer lede for the module sub-page hero */
  lede: string;
  /** key capabilities */
  features: string[];
  /** standards this module speaks */
  standards: string[];
  /** product-frame caption (we don't ship real screenshots here) */
  screen: string;
  /** show in the homepage 3x3 grid */
  inGrid?: boolean;
  /** accent role: default blue, "safety" coral, "success" emerald */
  tone?: "default" | "safety" | "success";
  /** Launch status. Everything listed here ships today as part of the EDC. */
  status: ModuleStatus;
};

export const modules: Module[] = [
  {
    slug: "edc",
    name: "EDC & eCRF",
    short: "EDC / eCRF",
    pillar: "Capture",
    icon: FileText,
    tagline: "Design forms and capture clean data, fast.",
    lede:
      "Point the AI Designer at the protocol and it drafts the form; drag the fields yourself and it is built either way — no programming, no vendor ticket. Edit checks, derivations and visit schedules run from the first subject, so the data lands clean.",
    features: [
      "Visual eCRF designer with conditional logic and derivations",
      "Range, format, consistency and cross-form checks fire at entry and draft their own queries",
      "Protocol amendments deploy as a new version, without a rebuild or a pause at sites",
      "Excel, CSV and CDISC ODM-XML exports come from the same data model the forms are built on",
    ],
    standards: ["CDISC ODM", "21 CFR Part 11"],
    screen: "eCRF builder — Adverse Events form, Visit 2",
    inGrid: true,
    status: "live",
  },
  {
    slug: "signatures",
    name: "e-Signatures, Freeze & Lock",
    short: "Signatures",
    pillar: "Stay inspection-ready",
    icon: PenLine,
    tagline: "Regulated-grade signatures, freeze, and lock.",
    lede:
      "The gate no agent passes. Sign, freeze and lock without leaving the study, with controlled unlock requests and a continuous audit trail that records machine-drafted work and the person who accepted it in the same chain.",
    features: [
      "Part 11–style electronic signatures",
      "Data freeze and database lock with attribution",
      "Immutable, queryable audit trail",
      "Controlled unlock requests with approvals",
    ],
    standards: ["21 CFR Part 11", "ICH E6(R3)"],
    screen: "Audit trail — signature and lock history",
    tone: "success",
    status: "live",
  },
  {
    slug: "analytics",
    name: "Reports & Export",
    short: "Analytics",
    pillar: "Close out & analyze",
    icon: BarChart3,
    tagline: "Reports, exports, and database lock.",
    lede:
      "Close the database and hand statistics a clean export, with every value traceable to the person or agent that entered it. Reporting, Excel, CSV and CDISC ODM-XML exports and a controlled database-lock workflow, all inside the platform.",
    features: [
      "Reports and analytics across the trial data model",
      "Excel, CSV and ODM-XML exports, each with a checksum",
      "Exports on request, for the whole study, chosen sites or chosen participants",
      "Controlled database-lock and closeout workflow",
    ],
    standards: ["CDISC ODM", "21 CFR Part 11"],
    screen: "Analytics — enrollment and query trends",
    tone: "success",
    status: "live",
  },
];

export const moduleBySlug = (slug: string) => modules.find((m) => m.slug === slug);
export const gridModules = modules.filter((m) => m.inGrid);
export const liveModules = modules.filter((m) => m.status === "live");

/* --------------------------------------------------------------------------
 * Integration catalogue
 *
 * Trialion sells one product, the EDC. Everything a trial needs around it —
 * consent, patient-reported outcomes, randomisation, site and document
 * management, safety, coding, central monitoring — is a system the customer
 * already runs, and the EDC works beside it. These describe how, one entry per
 * category: today data moves by entry on a form, CSV import or export, and the
 * /integrations page marks each live integration as in the 6-month pipeline, so
 * the descriptions do not repeat it. Partners are named by category rather than
 * by vendor, because this site does not put other companies' names in its copy.
 * ------------------------------------------------------------------------ */

export type IntegrationCategory = {
  slug: string;
  /** What the customer calls it — the noun on the tile. */
  name: string;
  /** The category of system on the other side of the connection. */
  category: string;
  /** How it is delivered, in customer-facing terms. */
  partners: string[];
  description: string;
  /** Standards the connection speaks, where there is one worth naming. */
  standards?: string[];
};

export const integrationCategories: IntegrationCategory[] = [
  {
    slug: "ctms",
    name: "CTMS",
    category: "CTMS systems",
    partners: ["Your existing CTMS", "In-house CRO operations systems"],
    description:
      "Site activation, milestones and monitoring visits stay in the CTMS you run. Sites and participants come into Trialion by CSV import, and study data goes back by export.",
  },
  {
    slug: "etmf",
    name: "eTMF",
    category: "eTMF systems",
    partners: ["Your existing eTMF", "Shared-drive document rooms", "In-house TMF systems"],
    description:
      "Your TMF stays in your eTMF, and Trialion's document register versions and controls the documents a study needs inside the EDC.",
  },
  {
    slug: "econsent",
    name: "eConsent",
    category: "eConsent platforms",
    partners: ["Established eConsent providers", "Paper-based ICF flows"],
    description:
      "Informed consent runs on the eConsent platform you choose, or on paper, and Trialion records each participant's consent and re-consent on its audit trail.",
  },
  {
    slug: "epro",
    name: "ePRO / eCOA",
    category: "ePRO / eCOA platforms",
    partners: ["Established ePRO / eCOA providers"],
    description:
      "Patient-reported outcomes and clinical assessments run on the ePRO or eCOA platform your study uses, and the results can be entered on a form.",
  },
  {
    slug: "rtsm",
    name: "RTSM / IWRS",
    category: "RTSM / IWRS systems",
    partners: ["Established RTSM / IWRS providers", "In-house RTSM workflows"],
    description:
      "Allocation and trial supply stay in your RTSM or IWRS, which keeps the log as the source. The assigned arm is entered in Trialion, which issues the next randomisation number, and dispensing is recorded on a form.",
  },
  {
    slug: "safety",
    name: "Safety database",
    category: "Safety databases",
    partners: ["Your existing safety database"],
    description:
      "Serious events are recorded in the EDC with a reporting clock, follow-ups and medical review. The expedited report goes from your safety database, and a manual reconciliation record is kept on the trail.",
    standards: ["ICH E2A"],
  },
  {
    slug: "coding",
    name: "Medical coding",
    category: "Medical coding tools",
    partners: ["MedDRA and WHODrug dictionaries"],
    description:
      "Verbatim terms are coded in your MedDRA and WHODrug tooling, and the codes load back into the study data as a form field.",
  },
  {
    slug: "quality",
    name: "RBQM & central monitoring",
    category: "RBQM and central monitoring tools",
    partners: ["Established RBQM platforms", "In-house QMS"],
    description:
      "Your RBQM tool works from Trialion's exports, while targeted source verification and queries run inside the EDC.",
  },
];

export const integrationCategory = (slug: string): IntegrationCategory | undefined =>
  integrationCategories.find((c) => c.slug === slug);

/** Nav mega-menu grouping by pillar */
export const pillars: { name: string; blurb: string; slugs: string[] }[] = [
  { name: "Capture", blurb: "Agents build the study, data lands clean", slugs: ["edc"] },
  { name: "Comply & close out", blurb: "A person signs, locks, reports, exports", slugs: ["signatures", "analytics"] },
];

/** Role view switcher (mirrors the in-app role system, reframed for buyers) */
export const roles: { id: string; name: string; line: string; screen: string }[] = [
  {
    id: "sponsor",
    name: "Sponsor ClinOps",
    line: "See what needs attention, not what has already happened. Overdue visits, aging queries and lock readiness, without asking for a status deck.",
    screen: "Portfolio — what needs attention this week",
  },
  {
    id: "cro-pm",
    name: "CRO PM",
    line: "Every sponsor's study on the same platform. No integration work between one client and the next.",
    screen: "CRO workspace — milestones across sponsors",
  },
  {
    id: "dm",
    name: "Data Manager",
    line: "Query rate, verification and lock readiness on one connected data model. Nothing reconciled by hand.",
    screen: "Data review — open queries by form",
  },
  {
    id: "site-pi",
    name: "Site PI",
    line: "Sign off, review safety events and oversee your site from one login. No second system to learn.",
    screen: "Investigator review — pending signatures",
  },
  {
    id: "crc",
    name: "Site Coordinator",
    line: "Fewer clicks, fewer logins. Visit workflows that work on the phone already in your pocket.",
    screen: "Visit checklist — Subject SUBJ-00482",
  },
  {
    id: "pv",
    name: "PV / Safety",
    line: "The event is captured once, in the EDC, with its reporting clock, follow-ups and medical review. Reconciliation with your safety database is recorded on the audit trail.",
    screen: "Adverse events — SAE captured, handed off",
  },
];

/** The problem we replace — reframed for CRO realities. */
/**
 * Homepage trust band — concrete signals every CDSCO-aware buyer asks
 * about within the first ten minutes of a procurement call.
 */
/**
 * Specification numbers, not performance claims. Each one is true by
 * construction — a commitment we control, a published price, or a countable
 * fact about the architecture — so none of them needs a customer to vouch
 * for it. `against` says, in plain words, what it saves you.
 *
 * All four headline values are OUR number. An earlier version put "4–8 weeks"
 * in the second slot — the figure a study build costs by hand — which put a large
 * number where a reader expects a boast, in a row where the other three were
 * ours. The comparison belongs in `against`, not in the value.
 */
export const proofMetrics = [
  {
    value: "30 minutes",
    label: "To see it running on your own protocol",
    against: "No scoping call, no quote and no purchase order first.",
  },
  {
    // True by construction, like the rest of this band: the product docs
    // describe the test cycle and its three sign-offs.
    value: "3 sign-offs",
    label: "Before a study version goes live",
    against: "Tested in UAT first, then released. Nothing reaches a site untested.",
  },
  {
    value: "No code",
    label: "To build your forms, checks and visit schedule",
    // The industry baseline is measured, so cite it rather than estimating:
    // Tufts CSDD put study database build at 68.3 days in 2017 and 69.4 in
    // 2020 — a harder number than "4-8 weeks", and a worse one for the
    // incumbents.
    against: "Tufts CSDD measured the industry at 68.3 days in 2017, and 69.4 in 2020.",
  },
  {
    value: "One",
    label: "Data model behind every module",
    against: "Enter data once and it is everywhere. Nothing to match up later.",
  },
  {
    value: "Zero",
    label: "Extra fees per form, per query or per export",
    against: "The price is the same whether you collect 10 records or 10,000.",
  },
];

/**
 * Homepage FAQ. These are the questions an experienced clinical data manager
 * asked of the product, in their words.
 */
export const homeFaqs: { q: string; a: string }[] = [
  // Confident, short and positive, and still accurate: say what the product
  // does and how the need is met, and never describe as built what is still in
  // build (isolated test-subject data, the QC stage, field-level restriction,
  // model/prompt retention and blinding).
  {
    q: "What environments do I get?",
    a: "Every study moves draft \u2192 UAT \u2192 live, and configuration is promoted between them rather than edited in production.",
  },
  {
    q: "Can I amend a study with subjects already enrolled?",
    a: "Yes. Amendments version the study configuration and deploy without a rebuild, while sites keep entering data on the current version. Bring your own scenario to the demo and we will run it live.",
  },
  {
    q: "How is my study validated, separately from your platform?",
    a: "Every study's configuration goes through a UAT cycle with three sign-offs before release. Our validation documents support your validation for intended use, and the team shares them during evaluation.",
  },
  {
    q: "Can we configure roles beyond PM, DM, CRA, CRC and PI?",
    a: "Yes. Roles and permissions are configurable at organisation, study and site level, so your own titles map as they are.",
  },
  {
    q: "Is the AI optional?",
    a: "Yes. AI is switched on per organisation, a study can be built entirely by hand, and everything the AI drafts waits for a qualified person to accept it.",
  },
  {
    q: "What is retained about an AI-drafted form?",
    a: "The draft, every human change to it and who approved it, all on the audit trail.",
  },
  {
    q: "Which study designs is Trialion built for?",
    a: "Open-label Phase I\u2013IV and bioequivalence studies, including randomised designs with assignments from your IRT. Planning a blinded design? The team will review the fit with you first.",
  },
];

/**
 * The questions a buyer asks before the product ones. Asked first in the chat.
 * Every answer restates what the pricing, solutions, compliance and company
 * pages already say; no timelines, customer names or prices that they do not.
 */
export const businessFaqs: { q: string; a: string }[] = [
  {
    q: "How is Trialion priced?",
    a: "Quarterly subscriptions per trial, in three tiers: Launch for a single study, Scale for a multi-site portfolio, and Enterprise for custom needs. There are no per-form, per-query or per-export fees, and onboarding support is included on every tier. For a quote, the team prices against your study design.",
  },
  {
    q: "Who is Trialion for?",
    a: "CROs and sponsors running clinical trials: BA-BE specialists, generic-drug CROs, multi-therapy and global CROs, biotech and pharma sponsors, and academic and government groups.",
  },
  {
    q: "How do we get started?",
    a: "With a 30-minute walkthrough on the live platform, set up against a protocol you are about to start. You speak directly to the people who build it.",
  },
  {
    q: "Will Trialion work with our other systems?",
    a: "Yes. Trialion is the EDC, and your CTMS, eTMF, RTSM, safety database and coding tools keep running alongside it, with data moving by form entry, CSV import or export. Live CTMS and eTMF integrations are in our 6-month pipeline.",
  },
  {
    q: "Is Trialion certified, and do you have validation documents?",
    a: "Yes. Trialion holds the certifications required and is designed to the regulations that govern trial data, including the NDCT Rules 2019, Indian GCP, ICH E6(R3) and 21 CFR Part 11. We have our full validation documents, and the team shares everything during evaluation.",
  },
  {
    q: "Can we take our data with us if we leave?",
    a: "Yes. The full study exports on request, audit trail included, as Excel, CSV and CDISC ODM-XML.",
  },
  {
    q: "Who is behind Trialion?",
    a: "A team of BITS Pilani and IQVIA alumni, with clinical data leadership and engineering experience, based in Gurugram and Jaipur, India. The founding team joins the calls.",
  },
];

/**
 * Compliance / standards band.
 * Ordering matters — Indian frameworks lead, secondary global standards
 * follow. These are the regulations the platform is designed to. Certificates
 * are shared during evaluation, so name one here only once the company confirms it.
 */
export const complianceBadges = [
  "NDCT Rules, 2019",
  "Indian GCP",
  "ICMR (2017)",
  "DPDP Act 2023",
  "ICH E6(R3)",
  "Designed to 21 CFR Part 11",
  "CDISC ODM",
];

/** Integrations strip — eClinical stack, not the Western one. */
export const integrations = [
  "Lab reports through AI Capture",
  "IRT / IWRS",
  "Safety databases",
  "CDISC ODM-XML exports",
  "BI dashboards",
  "Data warehouses",
];

/** Solutions segments */
export type Segment = {
  slug: string;
  group: "audience" | "phase" | "therapy";
  name: string;
  lede: string;
  points: string[];
  /** The photograph and one idea on this segment's own page. */
  band: { src: string; alt: string; title: string; body: string; chips: string[] };
};

export const segments: Segment[] = [
  {
    slug: "ba-be-specialists",
    group: "audience",
    name: "Bioequivalence (BA-BE) specialists",
    lede:
      "Replicate designs and intensive PK sampling on one system. Tight visit windows, assignments and dispensing on the volunteer record, and clean exports from the same data model.",
    points: [
      "Visit schedules with day offsets and tight windows for dosing intervals and PK sampling.",
      "Assignments from your IRT and dispensing recorded on the volunteer record, on one audit trail.",
      "Excel, CSV and CDISC ODM-XML exports from the same data model, with a checksum on every file.",
    ],
    band: {
      src: "/photos/lab-tubes.webp",
      alt: "Gloved hands pipetting samples into a rack of tubes",
      title: "Every timed sample, in its window",
      body: "PK studies live or die on timing. Windows are set on the visit schedule, so coordinators can see what is due, and edit checks run on every save.",
      chips: ["Day offsets", "Tight windows"],
    },
  },
  {
    slug: "generics",
    group: "audience",
    name: "Generic-drug CROs",
    lede:
      "Short, focused trials, run at volume. Repeat protocol designs reused from the form library, priced per study or per month, with the whole portfolio on one data model.",
    points: [
      "Per-study or per-month subscription, no per-form fees.",
      "Rapid study setup for repeat protocol designs across formulations.",
      "Multi-study oversight across the portfolio on a single data model.",
    ],
    band: {
      src: "/photos/lab-pipette.webp",
      alt: "A pipette releasing a drop of liquid into a small glass vial",
      title: "The next formulation starts from the last one",
      body: "When protocols repeat, your work should too. Forms saved to the library carry into the next study, so your team reviews what changed.",
      chips: ["Form library", "Repeat designs"],
    },
  },
  {
    slug: "multi-therapy",
    group: "audience",
    name: "Multi-therapy CROs",
    lede:
      "Phase II to IV across therapeutic areas, on one data model. Configurable workflows and a single continuous audit trail, whatever the indication.",
    points: [
      "Multi-site scale across major centres and remote sites alike.",
      "Targeted source verification built in, with your central monitoring tools working from clean exports.",
      "Visit schedules and form libraries shaped to each therapeutic area.",
    ],
    band: {
      src: "/photos/clinicians-monitors.webp",
      alt: "Two clinicians in white coats reviewing images on a row of monitors",
      title: "One way of working across every indication",
      body: "Move a data manager from oncology to cardiology and they already know the system, the roles and the review workflow. Only the study design changes.",
      chips: ["Same workflow", "Any indication"],
    },
  },
  {
    slug: "global-cros",
    group: "audience",
    name: "Global CRO networks",
    lede:
      "Sponsor-grade quality without disrupting the tooling your global team already runs. Local submission support where the study needs it.",
    points: [
      "The same continuous audit trail a global sponsor expects from any EDC.",
      "CSV import and standard exports today, with CTMS and eTMF integrations in our 6-month pipeline.",
    ],
    band: {
      src: "/photos/laptop-dim.webp",
      alt: "A laptop open in dim, warm light",
      title: "A regional arm that fits the wider programme",
      body: "Your global team keeps its tools and receives clean, standard exports, while the regional team works from one record with local regulations designed in.",
      chips: ["Standard exports", "Local rules designed in"],
    },
  },
  {
    slug: "sponsors",
    group: "audience",
    name: "Biotech & pharma sponsors",
    lede:
      "Own your trial data end to end. DPDP Act 2023 aligned, with no vendor holding the master copy.",
    points: [
      "DPDP Act 2023 aligned consent and processing flows by default.",
      "CDISC ODM-XML exports from the same data model as the forms.",
      "Export the full study, audit trail included, whenever you ask.",
    ],
    band: {
      src: "/photos/qa-review.webp",
      alt: "A professional reviewing a printed report at a desk beside a laptop",
      title: "Oversight you can see for yourself",
      body: "Open the same record your CRO works from. Who changed what, when and why is there to read, with no status report in between.",
      chips: ["Who, when, why", "Your data, on request"],
    },
  },
  {
    slug: "academic-govt",
    group: "audience",
    name: "Academic & government trial groups",
    lede:
      "Grant-funded research without enterprise-CRO overhead. Stand a study up on a research budget and keep the audit trail a regulator expects.",
    points: [
      "Ethics-committee-aware workflows aligned to ICMR National Ethical Guidelines (2017).",
      "Investigator-friendly capture for resident-led and registrar-led trials.",
      "Pricing structured for grant-funded research.",
    ],
    band: {
      src: "/photos/lab-samples.webp",
      alt: "Two researchers in lab coats examining a blood sample tube",
      title: "Rigour that fits a research budget",
      body: "Residents and registrars can run capture from day one, and the principal investigator signs on the same audit trail a commercial study would use.",
      chips: ["Investigator-friendly", "Ethics records on file"],
    },
  },
  {
    slug: "by-phase",
    group: "phase",
    name: "By phase — BA-BE through IV",
    lede:
      "From bioequivalence studies and first-in-human dose escalation to post-marketing surveillance. The same schema carries the study through every phase.",
    points: [
      "BA-BE: tight visit windows, intensive PK sampling, dispensing on the volunteer record.",
      "Phase II/III: multi-site scale, edit checks at entry and targeted source verification.",
      "Phase IV: long-running follow-up at scale, with a light load on sites.",
    ],
    band: {
      src: "/photos/lab-tray.webp",
      alt: "Rows of sample tubes held in a laboratory tray",
      title: "One system as a molecule moves forward",
      body: "As a programme moves from one phase to the next, your forms, roles and habits move with it, so each new phase starts from what your team already knows.",
      chips: ["BA-BE", "Phase I to IV"],
    },
  },
  {
    slug: "by-therapy",
    group: "therapy",
    name: "By therapy area",
    lede:
      "Oncology, cardiology, infectious disease, rare disease and devices. Visit schedules and instrument libraries configured to your protocol.",
    points: [
      "Oncology: complex visit schedules and cross-form rules, configured to your protocol.",
      "Infectious disease: quick study setup and browser-based capture for remote sites.",
      "Device: investigation workflows designed to ISO 14155 and India's MD Rules, 2017.",
    ],
    band: {
      src: "/photos/site-tablet.webp",
      alt: "A nurse and a doctor looking at a tablet together",
      title: "Forms that speak your therapy area",
      body: "Fields, visit windows and checks follow your protocol's language, so coordinators recognise the study they are working on from the first screen.",
      chips: ["Your fields", "Your visit windows"],
    },
  },
];

export const segmentBySlug = (slug: string) => segments.find((s) => s.slug === slug);

/* --------------------------------------------------------------------------
 * Rich per-module detail — powers the module sub-pages (outcomes band,
 * "how it works" steps, and FAQs). Keyed by module slug so adding a module's
 * page is just adding data here.
 * ------------------------------------------------------------------------ */
export type ModuleDetail = {
  /** headline outcomes shown as a stat band */
  outcomes: { value: string; label: string }[];
  /** 3-step "how it works" flow */
  how: { step: string; body: string }[];
  /** module-specific FAQs */
  faqs: { q: string; a: string }[];

  /* --------------------------------------------------------------------
   * Optional marquee sections — populated only for hero modules (EDC).
   * The generic module sub-page template renders these conditionally;
   * other modules render the standard features grid and skip these.
   * ------------------------------------------------------------------ */

  /** Compact info chips shown directly under the hero CTAs. */
  heroStats?: string[];

  /** Capabilities grouped by stage — replaces the simple features grid. */
  capabilityGroups?: {
    stage: string;
    blurb: string;
    items: string[];
  }[];

  /** Screen tour — each entry renders as a ProductFrame thumbnail. */
  screens?: {
    title: string;
    caption: string;
  }[];

  /** Compliance & validation panel. */
  compliance?: {
    title: string;
    body: string;
    badges: string[];
  };

  /** Migration callout — "moving from your existing EDC". */
  migration?: {
    title: string;
    body: string;
    from: string[];
    weeks: string;
  };
};

export const moduleDetails: Record<string, ModuleDetail> = {
  edc: {
    outcomes: [
      { value: "Fewer", label: "manual queries — edit checks fire at entry" },
      { value: "3 sign-offs", label: "before a study version goes live" },
      { value: "ODM-XML", label: "export from the same data model as the forms" },
    ],
    how: [
      { step: "Design", body: "Build forms, edit checks, derivations, and visit schedules in a visual designer — no programming required." },
      { step: "Capture", body: "Sites enter data with real-time validation that catches errors at the point of entry, not weeks later." },
      { step: "Review", body: "Data managers resolve queries inline, with every change recorded on one continuous audit trail." },
    ],
    heroStats: [
      "Live today",
      "Two-person database lock",
      "Aligned to the NDCT Rules, 2019",
      "Sandbox on your protocol",
    ],
    capabilityGroups: [
      {
        stage: "Design",
        blurb: "Visual study build, no programming.",
        items: [
          "Drag-and-drop eCRF designer with conditional logic",
          "Pre-built BA-BE templates (replicate-design, PK sampling)",
          "Visit schedules for parallel, crossover, and adaptive designs",
          "Edit checks and derivations without writing code",
          "Protocol amendments deploy as a new version without a rebuild; sites keep entering data, and each data point records the version it was captured under",
        ],
      },
      {
        stage: "Capture",
        blurb: "Clean data from the first entry.",
        items: [
          "Range, format, consistency and cross-form checks run at the point of entry, not in a monitoring pass weeks later",
          "A failing value drafts its own query with subject, site, visit and field attached; nothing is sent until a data manager reviews it",
          "Mobile-first capture for coordinators at low-connectivity sites",
          "Offline capture that syncs cleanly on reconnect at low-bandwidth sites",
        ],
      },
      {
        stage: "Review",
        blurb: "Data managers stay ahead of the queue.",
        items: [
          "Inline query resolution across forms and visits",
          "Targeted source verification against a verification plan",
          "Separation of duties on every query: raise, answer and close",
          "Open, answered and closed queries in one workflow",
        ],
      },
      {
        stage: "Lock & Export",
        blurb: "A controlled close-out and a clean export.",
        items: [
          "Controlled data freeze and database lock workflows",
          "21 CFR Part 11–style e-signatures with full attribution",
          "Excel, CSV and CDISC ODM-XML exports from the same data model",
          "Exports on request, for the whole study, chosen sites or chosen participants",
        ],
      },
    ],
    screens: [
      { title: "eCRF Builder", caption: "Drag-and-drop form designer — Adverse Events, Visit 2." },
      { title: "Data review", caption: "Open queries by form and visit." },
      { title: "Audit trail", caption: "Continuous, immutable audit across every action." },
      { title: "Data export", caption: "Excel, CSV and ODM-XML, each with a checksum." },
    ],
    compliance: {
      title: "Built for audit, from day one.",
      body:
        "The Trialion EDC is designed for Indian GCP and aligned to CDSCO New Drugs and Clinical Trials Rules, 2019. Role-based access, electronic signatures, and an immutable audit trail are built into the platform from the outset. Personal data is processed in alignment with the DPDP Act 2023, with data encrypted in transit and stored files encrypted at rest. Trialion holds the certifications required, and our validation documents support your validation for intended use.",
      badges: [
        "NDCT Rules, 2019",
        "Indian GCP",
        "ICMR (2017)",
        "DPDP Act 2023",
        "ICH E6(R3)",
        "Designed to 21 CFR Part 11",
        "CDISC ODM",
      ],
    },
    migration: {
      title: "Migrating from your existing EDC?",
      body:
        "Our migration team runs a standard 4–6 week playbook for moving studies — including bioequivalence volunteer records, eCRFs, queries, and the audit trail — from your existing EDC or paper-based workflows onto Trialion. Most migrations proceed without pausing site activity.",
      from: [
        "Commercial cloud EDCs",
        "Self-hosted open-source EDCs",
        "In-house built capture systems",
        "Spreadsheets and paper CRFs",
      ],
      weeks: "4–6 weeks",
    },
    faqs: [
      {
        q: "Where is the data hosted?",
        a: "Customer data is processed in alignment with the DPDP Act 2023. Data is encrypted in transit and stored files are encrypted at rest; access is controlled by role and site, and every change, sign-in and signature is written to the audit trail. The full subprocessor list is published in our Data Processing Agreement.",
      },
      {
        q: "How does this work for coordinators at low-connectivity sites?",
        a: "Mobile-first capture stores entries locally when connectivity is unreliable and synchronises on reconnect. Audit attribution preserves the original capture timestamp. The same workflow operates from a laptop at a metro site or a smartphone at a district hospital.",
      },
      {
        q: "Do sites need to install anything?",
        a: "No. Trialion is browser-based — Chrome, Edge, Safari, and Firefox — on desktop, tablet or phone. No client software, no per-machine deployment.",
      },
      {
        q: "How fast can we see it on our own protocol?",
        a: "We set up a sandbox configured against your study design before the walkthrough. Book a demo and our Gurugram team handles the intake.",
      },
      {
        q: "How is this priced?",
        a: "Per active study or per month. No per-form, per-query, or per-export fees. See /pricing for tiers.",
      },
    ],
  },
};

export const moduleDetail = (slug: string): ModuleDetail | undefined => moduleDetails[slug];
